import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getEmployees, getAbsences, createAbsence, updateAbsenceStatus, getCantonHolidays } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Calendar,
  Plus,
  Check,
  X,
  Clock,
  Umbrella,
  Heart,
  Plane,
  Baby,
  Shield,
  AlertCircle,
  CalendarDays,
  Users,
  Sun,
  ChevronLeft,
  ChevronRight,
  Flag,
} from 'lucide-react';

const AbsencesPage = () => {
  const { t } = useLanguage();
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [selectedCanton, setSelectedCanton] = useState('GE');
  
  const [formData, setFormData] = useState({
    employee_id: '',
    type: 'vacation',
    start_date: '',
    end_date: '',
    half_day_start: false,
    half_day_end: false,
    reason: '',
  });

  const absenceTypes = [
    { value: 'vacation', label: 'Vacances', icon: Plane, color: 'bg-blue-100 text-blue-800' },
    { value: 'sick_leave', label: 'Maladie', icon: Heart, color: 'bg-red-100 text-red-800' },
    { value: 'accident', label: 'Accident', icon: AlertCircle, color: 'bg-orange-100 text-orange-800' },
    { value: 'maternity', label: 'Maternité', icon: Baby, color: 'bg-pink-100 text-pink-800' },
    { value: 'paternity', label: 'Paternité', icon: Baby, color: 'bg-purple-100 text-purple-800' },
    { value: 'military', label: 'Service militaire', icon: Shield, color: 'bg-green-100 text-green-800' },
    { value: 'unpaid', label: 'Congé sans solde', icon: Clock, color: 'bg-gray-100 text-gray-800' },
    { value: 'other', label: 'Autre', icon: Calendar, color: 'bg-slate-100 text-slate-800' },
  ];

  const cantons = [
    { code: 'AG', name: 'Argovie' }, { code: 'AI', name: 'Appenzell Rh.-Int.' },
    { code: 'AR', name: 'Appenzell Rh.-Ext.' }, { code: 'BE', name: 'Berne' },
    { code: 'BL', name: 'Bâle-Campagne' }, { code: 'BS', name: 'Bâle-Ville' },
    { code: 'FR', name: 'Fribourg' }, { code: 'GE', name: 'Genève' },
    { code: 'GL', name: 'Glaris' }, { code: 'GR', name: 'Grisons' },
    { code: 'JU', name: 'Jura' }, { code: 'LU', name: 'Lucerne' },
    { code: 'NE', name: 'Neuchâtel' }, { code: 'NW', name: 'Nidwald' },
    { code: 'OW', name: 'Obwald' }, { code: 'SG', name: 'Saint-Gall' },
    { code: 'SH', name: 'Schaffhouse' }, { code: 'SO', name: 'Soleure' },
    { code: 'SZ', name: 'Schwytz' }, { code: 'TG', name: 'Thurgovie' },
    { code: 'TI', name: 'Tessin' }, { code: 'UR', name: 'Uri' },
    { code: 'VD', name: 'Vaud' }, { code: 'VS', name: 'Valais' },
    { code: 'ZG', name: 'Zoug' }, { code: 'ZH', name: 'Zurich' },
  ];

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonth, selectedCanton]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [absRes, empRes, holRes] = await Promise.all([
        getAbsences({ year: selectedYear, month: selectedMonth }),
        getEmployees({ active_only: true }),
        getCantonHolidays(selectedCanton, selectedYear)
      ]);
      setAbsences(absRes.data || []);
      setEmployees(empRes.data || []);
      setHolidays(holRes.data?.holidays || []);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status !== 404) {
        toast.error(t('hr.loadError') || 'Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_id || !formData.start_date || !formData.end_date) {
      toast.error(t('hr.fillRequired') || 'Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await createAbsence(formData);
      toast.success(t('hr.absenceCreated') || 'Demande d\'absence créée');
      setShowForm(false);
      setFormData({
        employee_id: '',
        type: 'vacation',
        start_date: '',
        end_date: '',
        half_day_start: false,
        half_day_end: false,
        reason: '',
      });
      loadData();
    } catch (error) {
      console.error('Error creating absence:', error);
      toast.error(error.response?.data?.detail || t('hr.createError') || 'Erreur lors de la création');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateAbsenceStatus(id, newStatus);
      toast.success(
        newStatus === 'approved' 
          ? (t('hr.absenceApproved') || 'Absence approuvée')
          : (t('hr.absenceRejected') || 'Absence refusée')
      );
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(t('hr.updateError') || 'Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'En attente', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approuvée', icon: Check },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Refusée', icon: X },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Annulée', icon: X },
    };
    const { color, label, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = absenceTypes.find(t => t.value === type) || absenceTypes[absenceTypes.length - 1];
    const Icon = typeConfig.icon;
    return (
      <Badge className={`${typeConfig.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {typeConfig.label}
      </Badge>
    );
  };

  const months = [
    { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' }, { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' }, { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' },
  ];

  const years = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - 1 + i);

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month - 1, 1).getDay();
  
  const isHoliday = (day) => {
    return holidays.some(h => {
      const hDate = new Date(h.date);
      return hDate.getDate() === day && hDate.getMonth() + 1 === selectedMonth;
    });
  };

  const getHolidayName = (day) => {
    const h = holidays.find(h => {
      const hDate = new Date(h.date);
      return hDate.getDate() === day && hDate.getMonth() + 1 === selectedMonth;
    });
    return h?.name || '';
  };

  const isAbsent = (day, employeeId = null) => {
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return absences.some(a => {
      if (employeeId && a.employee_id !== employeeId) return false;
      if (a.status !== 'approved') return false;
      return a.start_date <= dateStr && a.end_date >= dateStr;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayDate = new Date(selectedYear, selectedMonth - 1, day);
      const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
      const holiday = isHoliday(day);
      const holidayName = getHolidayName(day);
      
      // Get absences for this day
      const dayAbsences = absences.filter(a => {
        if (a.status !== 'approved') return false;
        return a.start_date <= dateStr && a.end_date >= dateStr;
      });

      days.push(
        <div 
          key={day} 
          className={`h-24 border border-gray-100 p-1 overflow-hidden ${
            isWeekend ? 'bg-gray-50' : holiday ? 'bg-red-50' : 'bg-white'
          }`}
        >
          <div className="flex justify-between items-start">
            <span className={`text-sm font-medium ${
              isWeekend ? 'text-gray-400' : holiday ? 'text-red-600' : 'text-gray-700'
            }`}>
              {day}
            </span>
            {holiday && (
              <Flag className="w-3 h-3 text-red-500" title={holidayName} />
            )}
          </div>
          {holiday && (
            <div className="text-xs text-red-600 truncate" title={holidayName}>
              {holidayName}
            </div>
          )}
          <div className="mt-1 space-y-0.5">
            {dayAbsences.slice(0, 2).map((a, idx) => {
              const typeConfig = absenceTypes.find(t => t.value === a.type);
              return (
                <div 
                  key={idx} 
                  className={`text-xs px-1 py-0.5 rounded truncate ${typeConfig?.color || 'bg-gray-100'}`}
                  title={`${a.employee_name || 'Employé'} - ${typeConfig?.label || a.type}`}
                >
                  {a.employee_name?.split(' ')[0] || 'N/A'}
                </div>
              );
            })}
            {dayAbsences.length > 2 && (
              <div className="text-xs text-gray-500">+{dayAbsences.length - 2} autres</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  // Stats
  const pendingCount = absences.filter(a => a.status === 'pending').length;
  const approvedCount = absences.filter(a => a.status === 'approved').length;
  const totalDays = absences.filter(a => a.status === 'approved').reduce((sum, a) => sum + (a.working_days || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="absences-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-blue-600" />
            {t('hr.absencesPlanning') || 'Planning & Absences'}
          </h1>
          <p className="text-slate-500 mt-1">
            {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <Users className="w-4 h-4 mr-1" />
            Liste
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
          >
            <Calendar className="w-4 h-4 mr-1" />
            Calendrier
          </Button>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-absence-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('hr.newAbsence') || 'Nouvelle absence'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Select 
                value={selectedMonth.toString()} 
                onValueChange={(v) => setSelectedMonth(parseInt(v))}
              >
                <SelectTrigger className="w-[140px]" data-testid="filter-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select 
                value={selectedYear.toString()} 
                onValueChange={(v) => setSelectedYear(parseInt(v))}
              >
                <SelectTrigger className="w-[100px]" data-testid="filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Flag className="w-4 h-4 text-red-500" />
              <Select 
                value={selectedCanton} 
                onValueChange={setSelectedCanton}
              >
                <SelectTrigger className="w-[160px]" data-testid="filter-canton">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cantons.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.pendingRequests') || 'En attente'}</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.approved') || 'Approuvées'}</p>
                <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
              </div>
              <Check className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.totalAbsenceDays') || 'Jours d\'absence'}</p>
                <p className="text-2xl font-bold text-blue-600">{totalDays}</p>
              </div>
              <CalendarDays className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.publicHolidays') || 'Jours fériés'}</p>
                <p className="text-2xl font-bold text-red-600">{holidays.length}</p>
              </div>
              <Flag className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Public Holidays List */}
      {holidays.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Flag className="w-4 h-4 text-red-500" />
              {t('hr.publicHolidaysIn') || 'Jours fériés en'} {cantons.find(c => c.code === selectedCanton)?.name} ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {holidays.map((h, idx) => (
                <Badge key={idx} variant="outline" className="text-red-700 border-red-200 bg-red-50">
                  <Sun className="w-3 h-3 mr-1" />
                  {h.name} - {new Date(h.date).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' })}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Content - List or Calendar */}
      {viewMode === 'list' ? (
        <Card>
          <CardContent className="p-0">
            {absences.length === 0 ? (
              <div className="p-12 text-center">
                <CalendarDays className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {t('hr.noAbsences') || 'Aucune absence'}
                </h3>
                <p className="text-slate-500 mb-4">
                  {t('hr.noAbsencesThisMonth') || 'Aucune demande d\'absence ce mois-ci'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('hr.employee') || 'Employé'}</TableHead>
                    <TableHead>{t('hr.type') || 'Type'}</TableHead>
                    <TableHead>{t('hr.period') || 'Période'}</TableHead>
                    <TableHead className="text-center">{t('hr.days') || 'Jours'}</TableHead>
                    <TableHead>{t('hr.status') || 'Statut'}</TableHead>
                    <TableHead className="text-right">{t('common.actions') || 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {absences.map((absence) => (
                    <TableRow key={absence.id} data-testid={`absence-row-${absence.id}`}>
                      <TableCell>
                        <div className="font-medium">{absence.employee_name || 'N/A'}</div>
                      </TableCell>
                      <TableCell>{getTypeBadge(absence.type)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(absence.start_date).toLocaleDateString('fr-CH')}
                          {absence.start_date !== absence.end_date && (
                            <> → {new Date(absence.end_date).toLocaleDateString('fr-CH')}</>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{absence.working_days || 1}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(absence.status)}</TableCell>
                      <TableCell className="text-right">
                        {absence.status === 'pending' && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => handleStatusChange(absence.id, 'approved')}
                              data-testid={`approve-absence-${absence.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleStatusChange(absence.id, 'rejected')}
                              data-testid={`reject-absence-${absence.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-4">
            {/* Calendar Header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {renderCalendar()}
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 bg-red-50 border border-red-200 rounded"></div>
                <span className="text-slate-600">Jour férié</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 bg-gray-50 border border-gray-200 rounded"></div>
                <span className="text-slate-600">Weekend</span>
              </div>
              {absenceTypes.slice(0, 4).map((type) => (
                <div key={type.value} className="flex items-center gap-2 text-sm">
                  <div className={`w-4 h-4 rounded ${type.color.replace('text-', 'border-')}`}></div>
                  <span className="text-slate-600">{type.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Absence Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-blue-600" />
              {t('hr.newAbsence') || 'Nouvelle demande d\'absence'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t('hr.employee') || 'Employé'} *</Label>
              <Select 
                value={formData.employee_id} 
                onValueChange={(v) => handleInputChange('employee_id', v)}
              >
                <SelectTrigger data-testid="select-employee">
                  <SelectValue placeholder={t('hr.selectEmployee') || 'Sélectionner un employé'} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('hr.absenceType') || 'Type d\'absence'}</Label>
              <Select 
                value={formData.type} 
                onValueChange={(v) => handleInputChange('type', v)}
              >
                <SelectTrigger data-testid="select-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {absenceTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('hr.startDate') || 'Date de début'} *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  required
                  data-testid="input-start-date"
                />
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    id="half_start"
                    checked={formData.half_day_start}
                    onCheckedChange={(checked) => handleInputChange('half_day_start', checked)}
                  />
                  <Label htmlFor="half_start" className="text-sm font-normal">Demi-journée</Label>
                </div>
              </div>
              <div>
                <Label>{t('hr.endDate') || 'Date de fin'} *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  required
                  data-testid="input-end-date"
                />
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    id="half_end"
                    checked={formData.half_day_end}
                    onCheckedChange={(checked) => handleInputChange('half_day_end', checked)}
                  />
                  <Label htmlFor="half_end" className="text-sm font-normal">Demi-journée</Label>
                </div>
              </div>
            </div>

            <div>
              <Label>{t('hr.reason') || 'Motif'}</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                placeholder={t('hr.reasonPlaceholder') || 'Motif optionnel...'}
                rows={3}
                data-testid="input-reason"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                {t('common.cancel') || 'Annuler'}
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-absence-btn">
                <Check className="w-4 h-4 mr-2" />
                {t('hr.submitRequest') || 'Soumettre la demande'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AbsencesPage;
